import React from 'react';
import { Box, Typography, Paper, Divider, Chip, Grid } from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import WebIcon from '@mui/icons-material/Web';
import ApiIcon from '@mui/icons-material/Api';
import SearchIcon from '@mui/icons-material/Search';
import MapIcon from '@mui/icons-material/Map';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import BalanceIcon from '@mui/icons-material/Balance';
import CloudIcon from '@mui/icons-material/Cloud';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import LanguageIcon from '@mui/icons-material/Language';
import DataObjectIcon from '@mui/icons-material/DataObject';
import SecurityIcon from '@mui/icons-material/Security';
import LockIcon from '@mui/icons-material/Lock';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { MONO } from '../theme';

// Reusable component for architecture boxes
const ArchBox = ({ icon, title, subtitle, color, items }) => (
  <Paper
    elevation={2}
    sx={{
      p: 2,
      bgcolor: color,
      borderRadius: 2,
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      {icon}
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
    </Box>
    {subtitle && (
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>{subtitle}</Typography>
    )}
    {items && (
      <Box sx={{ mt: 'auto' }}>
        {items.map((item, idx) => (
          <Chip key={idx} label={item} size="small" sx={{ m: 0.25, fontSize: '0.7rem' }} />
        ))}
      </Box>
    )}
  </Paper>
);

// Arrow connector component
const ArrowConnector = ({ direction = 'right', label }) => (
  <Box sx={{
    display: 'flex',
    flexDirection: direction === 'down' ? 'column' : 'row',
    alignItems: 'center',
    justifyContent: 'center',
    p: 1
  }}>
    {direction === 'down' ? (
      <ArrowDownwardIcon sx={{ color: '#9e9e9e', fontSize: 30 }} />
    ) : direction === 'both' ? (
      <SyncAltIcon sx={{ color: '#9e9e9e', fontSize: 30 }} />
    ) : (
      <ArrowForwardIcon sx={{ color: '#9e9e9e', fontSize: 30 }} />
    )}
    {label && (
      <Typography variant="caption" color="text.secondary" sx={{ ml: direction === 'down' ? 0 : 1 }}>
        {label}
      </Typography>
    )}
  </Box>
);

// Data index card
const IndexCard = ({ icon, title, indices, color }) => (
  <Paper elevation={1} sx={{ p: 2, borderLeft: 4, borderColor: color, height: '100%' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      {icon}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color }}>{title}</Typography>
    </Box>
    {indices.map((idx, i) => (
      <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontFamily: MONO, fontSize: '0.8rem' }}>
          {idx.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {idx.count}
        </Typography>
      </Box>
    ))}
  </Paper>
);

export default function Documentation() {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <AccountBalanceIcon sx={{ fontSize: 40, color: '#1565c0' }} />
        <Box>
          <Typography variant="h4">
            ParlSearch System Documentation
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            High-Level Architecture & Design
          </Typography>
        </Box>
      </Box>

      {/* Overview Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1565c0' }}>
          Overview
        </Typography>
        <Typography variant="body1" paragraph>
          ParlSearch is a comprehensive search platform for Australian Parliamentary data. It provides
          full-text search capabilities across parliamentary records, enabling researchers, journalists,
          and citizens to explore the workings of Australia's federal parliament.
        </Typography>
        <Typography variant="body1" paragraph>
          The system indexes over <strong>120,000 documents</strong> across 22 different data types,
          including parliamentarian profiles, Hansard transcripts, bills, committee records, electoral
          boundaries, and parliamentary questions.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          <Chip label="Open Source" color="primary" variant="outlined" />
          <Chip label="Public Data" color="success" variant="outlined" />
          <Chip label="Full-Text Search" color="info" variant="outlined" />
          <Chip label="REST API" color="warning" variant="outlined" />
        </Box>
      </Paper>

      {/* System Architecture - Visual */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#1565c0' }}>
          System Architecture
        </Typography>

        {/* Client Layer */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Client Layer
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} sm={4}>
              <ArchBox
                icon={<LanguageIcon sx={{ color: '#1565c0' }} />}
                title="Web Browser"
                subtitle="User Interface"
                color="#e3f2fd"
                items={['Desktop', 'Mobile', 'Tablet']}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
          <ArrowConnector direction="both" label="HTTPS" />
        </Box>

        {/* CDN/Proxy Layer */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Edge Layer
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} sm={4}>
              <ArchBox
                icon={<SecurityIcon sx={{ color: '#ff9800' }} />}
                title="Cloudflare Tunnel"
                subtitle="Reverse Proxy & SSL"
                color="#fff3e0"
                items={['SSL/TLS', 'DDoS Protection', 'Caching']}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
          <ArrowConnector direction="down" />
        </Box>

        {/* Application Layer */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Application Layer
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <ArchBox
                icon={<WebIcon sx={{ color: '#1565c0' }} />}
                title="Frontend"
                subtitle="parlsearch.cc | Port 3000"
                color="#e3f2fd"
                items={['React 18', 'Material-UI', 'Leaflet', 'Axios']}
              />
            </Grid>
            <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ textAlign: 'center' }}>
                <SyncAltIcon sx={{ color: '#9e9e9e', fontSize: 40 }} />
                <Typography variant="caption" display="block" color="text.secondary">
                  REST API
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  JSON
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <ArchBox
                icon={<ApiIcon sx={{ color: '#2e7d32' }} />}
                title="Backend"
                subtitle="api.parlsearch.cc | Port 8000"
                color="#e8f5e9"
                items={['FastAPI', 'Python 3.12', 'Uvicorn', 'Pydantic']}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
          <ArrowConnector direction="down" label="HTTP :9200" />
        </Box>

        {/* Data Layer */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Data Layer
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} sm={6}>
              <ArchBox
                icon={<StorageIcon sx={{ color: '#ed6c02' }} />}
                title="Elasticsearch 8.11"
                subtitle="Search Engine & Data Store"
                color="#fff3e0"
                items={['22 Indices', '120k+ Documents', 'Full-Text', 'Aggregations', 'Facets']}
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Data Pipeline - Visual */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#1565c0' }}>
          Data Ingestion Pipeline
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <Paper elevation={2} sx={{ p: 2, bgcolor: '#fce4ec', borderRadius: 2, textAlign: 'center' }}>
              <CloudIcon sx={{ fontSize: 40, color: '#c2185b', mb: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Data Sources</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">APH Website</Typography>
              <Typography variant="body2">Parliamentary Handbook</Typography>
              <Typography variant="body2">Hansard Archives</Typography>
              <Typography variant="body2">AEC Boundaries</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'center' }}>
            <ArrowForwardIcon sx={{ color: '#9e9e9e', fontSize: 40 }} />
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <DataObjectIcon sx={{ color: '#2e7d32' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Import Scripts</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={0.5}>
                {[
                  'import_handbook.py',
                  'import_hansard.py',
                  'import_bills.py',
                  'import_constitution.py',
                  'import_standing_orders.py',
                  'import_senate_qon.py',
                  'import_house_qiw.py',
                  'import_notice_papers.py',
                  'import_electorate_maps.py',
                  'import_data.py'
                ].map((script, idx) => (
                  <Grid item xs={6} key={idx}>
                    <Typography variant="caption" sx={{ fontFamily: MONO, fontSize: '0.7rem' }}>
                      {script}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'center' }}>
            <ArrowForwardIcon sx={{ color: '#9e9e9e', fontSize: 40 }} />
          </Grid>

          <Grid item xs={12} md={3}>
            <Paper elevation={2} sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 2, textAlign: 'center' }}>
              <StorageIcon sx={{ fontSize: 40, color: '#ed6c02', mb: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Elasticsearch</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">Bulk Indexing</Typography>
              <Typography variant="body2">Schema Mapping</Typography>
              <Typography variant="body2">Text Analysis</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Technology Stack */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1565c0' }}>
          Technology Stack
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <WebIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Frontend</Typography>
              </Box>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li><strong>React 18</strong> - UI framework</li>
                <li><strong>Material-UI 5</strong> - Component library</li>
                <li><strong>React Router v6</strong> - Client-side routing</li>
                <li><strong>Axios</strong> - HTTP client</li>
                <li><strong>Leaflet</strong> - Interactive maps</li>
                <li><strong>react-leaflet</strong> - React map bindings</li>
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ApiIcon color="success" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Backend</Typography>
              </Box>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li><strong>FastAPI</strong> - Web framework</li>
                <li><strong>Python 3.12</strong> - Runtime</li>
                <li><strong>Uvicorn</strong> - ASGI server</li>
                <li><strong>Pydantic</strong> - Data validation</li>
                <li><strong>elasticsearch-py</strong> - ES client</li>
                <li><strong>luqum</strong> - Lucene query parsing</li>
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <StorageIcon color="warning" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Infrastructure</Typography>
              </Box>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li><strong>Elasticsearch 8.11</strong> - Search engine</li>
                <li><strong>Docker</strong> - Containerization</li>
                <li><strong>Docker Compose</strong> - Orchestration</li>
                <li><strong>Cloudflare Tunnel</strong> - Reverse proxy</li>
                <li><strong>Linux</strong> - Host OS</li>
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* User Features */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1565c0' }}>
          User Features
        </Typography>
        <Typography variant="body1" paragraph>
          ParlSearch offers personalized features for registered users, enabling saved searches,
          customized preferences, and email notifications.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LockIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Authentication</Typography>
              </Box>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li>Email/password registration</li>
                <li>JWT token authentication</li>
                <li>Secure session management</li>
                <li>Refresh token rotation</li>
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <BookmarkIcon sx={{ color: '#ed6c02' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Saved Searches</Typography>
              </Box>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li>Save any search with custom name</li>
                <li>Re-run saved searches instantly</li>
                <li>Edit or delete saved searches</li>
                <li>Track search result counts</li>
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SettingsIcon sx={{ color: '#2e7d32' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Preferences</Typography>
              </Box>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li>Results per page (10-100)</li>
                <li>Default sort order</li>
                <li>Snippet length (short/medium/long)</li>
                <li>Theme preference</li>
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#fce4ec', borderRadius: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <NotificationsIcon sx={{ color: '#c2185b' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Email Alerts</Typography>
              </Box>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li>Create alerts from saved searches</li>
                <li>Configurable frequency</li>
                <li>Immediate, daily, or weekly</li>
                <li>View delivery history</li>
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Authentication Flow</Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
            <Grid container spacing={2} alignItems="center" justifyContent="center">
              <Grid item>
                <Chip label="Register/Login" color="primary" />
              </Grid>
              <Grid item>
                <ArrowForwardIcon sx={{ color: '#9e9e9e' }} />
              </Grid>
              <Grid item>
                <Chip label="JWT Access Token" color="success" />
              </Grid>
              <Grid item>
                <ArrowForwardIcon sx={{ color: '#9e9e9e' }} />
              </Grid>
              <Grid item>
                <Chip label="API Requests" color="info" />
              </Grid>
              <Grid item>
                <ArrowForwardIcon sx={{ color: '#9e9e9e' }} />
              </Grid>
              <Grid item>
                <Chip label="Refresh Token" color="warning" />
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Paper>

      {/* Admin Interface */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AdminPanelSettingsIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1565c0' }}>
            Admin Interface
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          A separate administrative interface for managing data imports and scrapers with real-time
          output streaming.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1565c0' }}>Capabilities</Typography>
              <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                <li>Execute 23 import scripts</li>
                <li>Execute 32 scraper scripts</li>
                <li>Real-time WebSocket output streaming</li>
                <li>Execution history tracking</li>
                <li>Password-protected access</li>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1565c0' }}>Architecture</Typography>
              <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                <li>Separate FastAPI application (port 8001)</li>
                <li>React admin frontend (port 3001)</li>
                <li>JWT token authentication</li>
                <li>Subprocess management for script execution</li>
                <li>Cloudflare Tunnel integration</li>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2">
            Admin access:{' '}
            <a
              href="https://admin.parlsearch.cc"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#1565c0' }}
            >
              https://admin.parlsearch.cc
            </a>
          </Typography>
        </Box>
      </Paper>

      {/* Data Model - Visual Cards */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#1565c0' }}>
          Data Model
        </Typography>
        <Typography variant="body1" paragraph>
          The system uses 22 Elasticsearch indices organized into logical categories:
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={4}>
            <IndexCard
              icon={<PersonIcon sx={{ color: '#1565c0' }} />}
              title="People & Representation"
              color="#1565c0"
              indices={[
                { name: 'parliamentarians', count: '1,877' },
                { name: 'electorates', count: '219' },
                { name: 'parties', count: '57' },
                { name: 'electorate_maps', count: '1,962' },
                { name: 'parliamentarian_photos', count: '1,878' }
              ]}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <IndexCard
              icon={<AccountBalanceIcon sx={{ color: '#7b1fa2' }} />}
              title="Parliamentary Structure"
              color="#7b1fa2"
              indices={[
                { name: 'parliaments', count: '48' },
                { name: 'committees', count: '465' },
                { name: 'ministries', count: '75' },
                { name: 'shadowministries', count: '47' },
                { name: 'states', count: '9' }
              ]}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <IndexCard
              icon={<DescriptionIcon sx={{ color: '#2e7d32' }} />}
              title="Parliamentary Proceedings"
              color="#2e7d32"
              indices={[
                { name: 'hansard', count: '11,065' },
                { name: 'bills', count: '2,240' },
                { name: 'tabled_documents', count: '34,258' },
                { name: 'hor_divisions', count: '131' },
                { name: 'notice_papers', count: '2,944' }
              ]}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <IndexCard
              icon={<QuestionAnswerIcon sx={{ color: '#ed6c02' }} />}
              title="Questions & Inquiries"
              color="#ed6c02"
              indices={[
                { name: 'questions_on_notice', count: '1,975' },
                { name: 'senate_qon', count: '100' },
                { name: 'house_qiw', count: '134' },
                { name: 'parliament_calendar', count: '4,449' }
              ]}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <IndexCard
              icon={<WorkHistoryIcon sx={{ color: '#c2185b' }} />}
              title="Service Records"
              color="#c2185b"
              indices={[
                { name: 'recordsofservice', count: '59,248' },
                { name: 'ministryrecords', count: '5,318' },
                { name: 'shadowministryrecords', count: '3,167' },
                { name: 'recordsofservicetype', count: '22' }
              ]}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <IndexCard
              icon={<BalanceIcon sx={{ color: '#0277bd' }} />}
              title="Legal Framework"
              color="#0277bd"
              indices={[
                { name: 'constitution', count: '145' },
                { name: 'standing_orders', count: '908' },
                { name: 'hansard_toc', count: '41' },
                { name: 'standing_orders_toc', count: '1' }
              ]}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Search Capabilities */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SearchIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1565c0' }}>
            Search Capabilities
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          ParlSearch supports full Lucene query syntax, enabling powerful and precise searches:
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Query Syntax Examples</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ fontFamily: MONO, fontSize: '0.85rem' }}>
                <Box sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'ledger.accentText' }}>Boolean</Typography>
                  <Typography variant="body2" sx={{ fontFamily: MONO }}>
                    senator:"Murray Watt" AND portfolio:Health
                  </Typography>
                </Box>
                <Box sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'ledger.accentText' }}>Fuzzy</Typography>
                  <Typography variant="body2" sx={{ fontFamily: MONO }}>
                    senator:Watt~
                  </Typography>
                </Box>
                <Box sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'ledger.accentText' }}>Proximity</Typography>
                  <Typography variant="body2" sx={{ fontFamily: MONO }}>
                    topic:"mental health"~5
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ fontFamily: MONO, fontSize: '0.85rem' }}>
                <Box sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'ledger.accentText' }}>Wildcards</Typography>
                  <Typography variant="body2" sx={{ fontFamily: MONO }}>
                    senator:W*, portfolio:Hea?th
                  </Typography>
                </Box>
                <Box sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'ledger.accentText' }}>Date Range</Typography>
                  <Typography variant="body2" sx={{ fontFamily: MONO }}>
                    date:[2024-01-01 TO 2024-12-31]
                  </Typography>
                </Box>
                <Box sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'ledger.accentText' }}>Complex</Typography>
                  <Typography variant="body2" sx={{ fontFamily: MONO }}>
                    (senator:Watt OR senator:Wong) AND Health
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Search Features</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {[
            'Full-text search', 'Fuzzy matching', 'Wildcard queries', 'Faceted filtering',
            'Date range queries', 'Pagination', 'Field-specific search', 'Aggregations',
            'Cross-index search', 'Auto-complete', 'Highlighting'
          ].map((feature, idx) => (
            <Chip key={idx} label={feature} variant="outlined" size="small" />
          ))}
        </Box>
      </Paper>

      {/* API Overview */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1565c0' }}>
          API Structure
        </Typography>
        <Typography variant="body1" paragraph>
          The backend exposes 72+ RESTful API endpoints. All responses are JSON formatted.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Standard Response Format</Typography>
              <Box sx={{ fontFamily: MONO, fontSize: '0.8rem', bgcolor: '#263238', color: '#aed581', p: 2, borderRadius: 1 }}>
                <pre style={{ margin: 0, overflow: 'auto' }}>{`{
  "total": 1250,
  "hits": [...],
  "took_ms": 45,
  "query": "search term",
  "page": 1,
  "per_page": 25,
  "facets": {...}
}`}</pre>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Key Endpoints</Typography>
              <Box sx={{ fontFamily: MONO, fontSize: '0.75rem' }}>
                {[
                  { method: 'GET', path: '/api/search/global', desc: 'Cross-index search' },
                  { method: 'GET', path: '/api/parliamentarians/search', desc: 'Search MPs' },
                  { method: 'GET', path: '/api/hansard/search', desc: 'Search debates' },
                  { method: 'GET', path: '/api/bills/search', desc: 'Search legislation' },
                  { method: 'GET', path: '/api/electorate-maps/geojson-by-year/{year}', desc: 'Map data' },
                  { method: 'GET', path: '/health', desc: 'Health check' }
                ].map((ep, idx) => (
                  <Box key={idx} sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={ep.method} size="small" color="success" sx={{ fontSize: '0.65rem', height: 20 }} />
                    <Typography variant="caption" sx={{ fontFamily: MONO }}>{ep.path}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2">
            Full interactive API documentation:{' '}
            <a
              href="https://api.parlsearch.cc/docs"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#1565c0' }}
            >
              https://api.parlsearch.cc/docs
            </a>
          </Typography>
        </Box>
      </Paper>

      {/* Maps Feature */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <MapIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1565c0' }}>
            Electoral Maps
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          Interactive map feature showing historical electoral boundaries from 1901 to present.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1565c0' }}>Features</Typography>
              <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                <li>Interactive Leaflet-based map</li>
                <li>Historical boundary data (1901-present)</li>
                <li>Year-based timeline with animation</li>
                <li>State-based color coding</li>
                <li>Hover tooltips with electorate names</li>
                <li>Click-through to electorate details</li>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1565c0' }}>Technical Details</Typography>
              <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                <li>KML files parsed server-side</li>
                <li>GeoJSON served via REST API</li>
                <li>Boundary validation filters offshore territories</li>
                <li>Graph coloring for visual distinction</li>
                <li>Response caching (1 hour TTL)</li>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Deployment */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1565c0' }}>
          Deployment
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                borderRadius: 2
              }}
            >
              <WebIcon sx={{ fontSize: 40, color: '#1565c0', mb: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Frontend</Typography>
              <Typography variant="body2">Node 18 Alpine</Typography>
              <Chip label="Port 3000" size="small" sx={{ mt: 1 }} />
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                borderRadius: 2
              }}
            >
              <ApiIcon sx={{ fontSize: 40, color: '#2e7d32', mb: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Backend</Typography>
              <Typography variant="body2">Python 3.12 Slim</Typography>
              <Chip label="Port 8000" size="small" sx={{ mt: 1 }} />
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                borderRadius: 2
              }}
            >
              <StorageIcon sx={{ fontSize: 40, color: '#ed6c02', mb: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Elasticsearch</Typography>
              <Typography variant="body2">Version 8.11</Typography>
              <Chip label="Port 9200" size="small" sx={{ mt: 1 }} />
            </Paper>
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Production URLs</Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Frontend</Typography>
              <Typography variant="body2">
                <a href="https://parlsearch.cc" target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0' }}>
                  https://parlsearch.cc
                </a>
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">API</Typography>
              <Typography variant="body2">
                <a href="https://api.parlsearch.cc" target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0' }}>
                  https://api.parlsearch.cc
                </a>
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">API Docs</Typography>
              <Typography variant="body2">
                <a href="https://api.parlsearch.cc/docs" target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0' }}>
                  https://api.parlsearch.cc/docs
                </a>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Paper>

      {/* Footer */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        ParlSearch - Open access to Australian Parliamentary data
      </Typography>
    </Box>
  );
}
